import { useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { Container } from '@/components/ui/container'

import { AlertTriangleIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { useAuthStore } from '@/features/auth/store/store'
import { ExperienceVettingCriteriaExpertise } from '@/features/experience-vetting/components/experience-vetting-criteria-expertise.tsx'
import { ExperienceVettingCriteriaGallery } from '@/features/experience-vetting/components/experience-vetting-criteria-gallery.tsx'
import { ExperienceVettingCriteriaHero } from '@/features/experience-vetting/components/experience-vetting-criteria-hero.tsx'
import { ExperienceVettingCriteriaItinerary } from '@/features/experience-vetting/components/experience-vetting-criteria-itinerary.tsx'
import { ExperienceVettingCriteriaRejectedImages } from '@/features/experience-vetting/components/experience-vetting-criteria-rejected-images'
import { ExperienceVettingCriteriaSkeleton } from '@/features/experience-vetting/components/experience-vetting-criteria-skeleton'
import { useGetExperienceVetting } from '@/features/experience-vetting/queries/use-get-experience-vetting.ts'
import { checkExperienceErrors } from '@/features/experience-vetting/utils.tsx'
import type { OpsVettingURLParams } from '@/features/ops-vetting/types.ts'
import { SubHeaderBanner } from '@/features/photoset/components/sub-header-banner'
import { useExperienceVettingError } from '@/features/service-vetting/store/use-service-vetting-error'
import { useTranslation } from '@/translations/i18n'

export const ExperienceCriteriaVetting = () => {
	const { urlParams } = useOutletContext<{ urlParams: OpsVettingURLParams }>()
	const { t } = useTranslation()
	const { isPending, isError, remappedData } = useGetExperienceVetting(
		urlParams.id
	)
	const isTouched = useExperienceVettingError((state) => state.isTouched)
	const setShowBanner = useExperienceVettingError(
		(state) => state.setShowBanner
	)
	const showBanner = useExperienceVettingError((state) => state.showBanner)
	const setErrors = useExperienceVettingError((state) => state.setErrors)
	const resetErrors = useExperienceVettingError((state) => state.reset)
	const user = useAuthStore((state) => state.user)
	useEffect(() => {
		if (isTouched && remappedData) {
			const errorCheck = checkExperienceErrors(remappedData)
			setShowBanner(!errorCheck.isValid)
			setErrors(errorCheck.errors)
		}
	}, [remappedData, isTouched, setShowBanner, setErrors])

	useEffect(() => {
		return () => resetErrors()
	}, [resetErrors])

	const rejectedImages = useMemo(() => {
		return remappedData?.gallery.images.filter(
			(image) => image.isApproved === false
		)
	}, [remappedData])

	if (isPending) {
		return <ExperienceVettingCriteriaSkeleton />
	}

	if (isError) {
		return (
			<Container fullWidth>
				<ErrorBox
					icon={<AlertTriangleIcon />}
					title={t('errors:default')}
				/>
			</Container>
		)
	}
	const isReadOnly =
		remappedData?.status !== 'under_review' ||
		user?.role === 'read_only' ||
		!!remappedData?.isHostModified

	return (
		<ScrollArea scrollbar={<Scrollbar />}>
			{remappedData && (
				<Container fullWidth>
					<ExperienceVettingCriteriaHero experience={remappedData} />
				</Container>
			)}
			{showBanner && (
				<SubHeaderBanner
					subtitle={t(
						'vetting:criteria.commons.error_banner.subtitle'
					)}
					title={t('vetting:criteria.commons.error_banner.title')}
					type={'danger'}
				/>
			)}
			<Container fullWidth>
				{remappedData && (
					<div className='flex w-full flex-col gap-6'>
						<ExperienceVettingCriteriaExpertise
							experience={remappedData}
							isReadOnly={isReadOnly}
						/>
						<ExperienceVettingCriteriaItinerary
							experience={remappedData}
							isReadOnly={isReadOnly}
						/>
						<ExperienceVettingCriteriaGallery
							experience={remappedData}
							isReadOnly={isReadOnly}
						/>
						{rejectedImages && rejectedImages.length > 0 && (
							<ExperienceVettingCriteriaRejectedImages
								experience={remappedData}
								isReadOnly={isReadOnly}
							/>
						)}
					</div>
				)}
			</Container>
		</ScrollArea>
	)
}
ExperienceCriteriaVetting.displayName = 'ExperienceCriteriaVetting'
